import { getLogger } from '@logtape/logtape';

import { SERIES_INTERACTION_KEY } from '@/constants/availability-telemetry.ts';

import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { settleAvailabilityInteraction } from '@/lib/availability-telemetry.ts';

import { CollectionOccurrence, PreferredAvailability } from '@/models/appointment/Appointment.ts';
import { MetricFailureReason } from '@/models/metrics.ts';

import {
    SeriesAutoSuggestionParams,
    getSeriesAutoSuggestion,
    getSeriesAvailability,
} from '@/services/availabilityService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

import { selectCurrentForm, useBookingStore } from '@/stores/bookingStore.ts';

import { MAX_SERIES_SPAN_DAYS, getSeriesSpanInDays } from '@/utils/appointmentUtils.ts';
import { toAvailabilityFailureReason } from '@/utils/availability-utils.ts';
import { insuranceToInsuranceType } from '@/utils/insurance-utils.ts';

const logger = getLogger(['hook', 'series-availability']);

const GENERIC_AVAILABILITY_ERROR_MESSAGE = 'Fehler beim Laden der Verfügbarkeit. Bitte versuchen Sie es erneut.';

// The operator asked for slots and got none, so every refusal before the request is a failure of its own.
const refuse = (reason: MetricFailureReason): undefined => {
    settleAvailabilityInteraction({ key: SERIES_INTERACTION_KEY, outcome: 'failure', reason: reason });
    return undefined;
};

interface UseSeriesAvailabilityResult {
    fetchSeriesAvailability: () => Promise<CollectionOccurrence[] | undefined>;
    fetchSeriesAutoSuggestion: (startDateAnchor?: string) => Promise<CollectionOccurrence[] | undefined>;
}

export const useSeriesAvailability = (): UseSeriesAvailabilityResult => {
    const currentForm = useBookingStore(selectCurrentForm);
    const currentPatient = useBookingStore((state) => state.current_patient);
    const collectionPastAppointments = useBookingStore((state) => state.collectionPastAppointments);
    const editingCollectionId = useBookingStore((state) => state.editingCollectionId);
    const { selected_customer: selectedCustomer, selected_location: selectedLocation } = useConfig();
    const { data: enrichedData, is_loading: isPractitionerLoading } = useEnrichedPractitionerDataForSpecificInstance(
        currentPatient?.instance_id ?? ''
    );

    const resolveParams = (startDateAnchor?: string): SeriesAutoSuggestionParams | undefined => {
        const config = useBookingStore.getState().collection_configuration;
        if (!config) {
            showErrorNotification('Bitte konfigurieren Sie zuerst die Terminserie');
            return refuse('availability_config_incomplete');
        }

        if (getSeriesSpanInDays(config) > MAX_SERIES_SPAN_DAYS) {
            showErrorNotification('Die Terminserie darf ca. 5 Jahre (1800 Tage) nicht überschreiten.');
            return refuse('availability_span_too_long');
        }

        if (!currentForm) return refuse('availability_config_incomplete');

        const practitioner = enrichedData?.practitioners.find((p) =>
            p.calendars.some((c) => c.pvs.id === currentForm.calendar_pvs_id)
        );
        if (!practitioner) {
            showErrorNotification(
                isPractitionerLoading
                    ? 'Daten werden geladen. Bitte versuchen Sie es erneut.'
                    : 'Behandler konnte nicht ermittelt werden. Bitte Kalender erneut auswählen.'
            );
            return refuse('availability_no_calendar');
        }

        const appointmentType = enrichedData?.appointment_types.find(
            (t) => t.id === currentForm.apt_type_id_or_schedule_type_pvs_id
        );
        if (!appointmentType) return refuse('availability_config_incomplete');

        return {
            pvs_provider: currentForm.pvs_provider,
            customer_id: selectedCustomer?.customer_id ?? '',
            user_id: practitioner.id,
            location_key: selectedLocation?.key ?? '',
            appointment_type_key: appointmentType.prismic_key ?? '',
            appointment_type_id: appointmentType.id,
            insurance_type: insuranceToInsuranceType(currentForm.insurance),
            pvs_calendar_id: currentForm.calendar_pvs_id ?? '',
            is_online: appointmentType.is_online ?? false,
            is_returning_patient: useBookingStore.getState().is_returning_patient,
            start_date: config.start_date,
            interval_type: config.interval_type,
            interval_frequency: config.interval_frequency,
            number_of_appointments: config.number_of_appointments - collectionPastAppointments.length,
            preferred_availability: config.preferred_availability,
            preferred_time:
                config.preferred_availability === PreferredAvailability.FIXED ? config.preferred_time : undefined,
            collection_id: editingCollectionId,
            start_date_anchor: startDateAnchor,
        };
    };

    const runFetch = async (
        fetcher: (params: SeriesAutoSuggestionParams) => Promise<CollectionOccurrence[]>,
        errorContext: string,
        startDateAnchor?: string
    ): Promise<CollectionOccurrence[] | undefined> => {
        const params = resolveParams(startDateAnchor);
        if (!params) return undefined;
        try {
            return await fetcher(params);
        } catch (error) {
            logger.error('Series availability fetch failed', { context: errorContext, error: error });
            showErrorNotification(GENERIC_AVAILABILITY_ERROR_MESSAGE);
            return refuse(toAvailabilityFailureReason(error));
        }
    };

    const fetchSeriesAvailability = (): Promise<CollectionOccurrence[] | undefined> =>
        runFetch(getSeriesAvailability, 'Failed to get series availability:');

    const fetchSeriesAutoSuggestion = (startDateAnchor?: string): Promise<CollectionOccurrence[] | undefined> =>
        runFetch(getSeriesAutoSuggestion, 'Failed to get series auto-suggestion:', startDateAnchor);

    return { fetchSeriesAvailability, fetchSeriesAutoSuggestion };
};
